import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getCalendarSetup, saveProfessionalDetails } from "../api/api";
import LeftSidebar from "../components/sidebar";
import Header from "../components/Header";
import ProfileMenu from "../components/ProfileMenu";
import { useOutletContext } from "react-router-dom";


export default function ProfessionalSettingsReview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { routeLoading } = useOutletContext();

  const [options, setOptions] = useState({
    airlines: [],
    bases: [],
    aircrafts: [],
    roles: [],
    seats: [],
  });
  const [selected, setSelected] = useState({
    airline_uid: null,
    base_uid: null,
    aircraft_uid: null,
    role_uid: null,
    seat_uid: null,
  });
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    let mounted = true;

    getCalendarSetup()
      .then((res) => {
        if (!mounted) return;

        const data = res.data || {};
        const sel = data.selected || {};

        setSelected({
          airline_uid: sel.airline_uid || null,
          base_uid: sel.base_uid || null,
          aircraft_uid: sel.aircraft_uid || null,
          role_uid: sel.role_uid || null,
          seat_uid: sel.seat_uid || null,
        });

        setOptions(
          data.options || {
            airlines: [],
            bases: [],
            aircrafts: [],
            roles: [],
            seats: [],
          }
        );
      })
      .catch(() => setError("Failed to load professional settings"))
      .finally(() => setLoading(false));

    return () => {
      mounted = false;
    };
  }, []);

  const getLabel = (list, key, value) => {
    if (!value) return "";
    const found = list.find((item) => item[key] === value);
    return found || null;
  };

  const airline = getLabel(options.airlines, "uid", selected.airline_uid);
  const base = getLabel(options.bases, "uid", selected.base_uid);
  const aircraft = getLabel(options.aircrafts, "uid", selected.aircraft_uid);
  const role = getLabel(options.roles, "uid", selected.role_uid);
  const seat = getLabel(options.seats, "uid", selected.seat_uid);

  const airlineLabel = airline
    ? `${airline.name || ""}${airline.iata_code ? ` (${airline.iata_code})` : ""}`
    : "";
  const baseLabel = base
    ? `${base.airport_code || ""}${base.city ? ` - ${base.city}` : ""}`
    : "";
  const aircraftLabel = aircraft
    ? `${aircraft.model || ""}${aircraft.manufacturer ? ` - ${aircraft.manufacturer}` : ""}`
    : "";
  const roleLabel = role ? role.role_name || "" : "";
  const seatLabel = seat ? seat.seat_name || "" : "";

  const handleChange = (key) => (e) => {
    setSelected((prev) => ({
      ...prev,
      [key]: e.target.value || null,
    }));
  };

  const handleSave = async () => {
    const requiredFields = [
      "airline_uid",
      "base_uid",
      "aircraft_uid",
      "role_uid",
      "seat_uid",
    ];

    for (const field of requiredFields) {
      if (!selected[field]) {
        toast.error("Please select all required fields");
        return;
      }
    }

    try {
      setSaving(true);
      setError("");
      await saveProfessionalDetails({ ...selected });
      toast.success("Professional details saved successfully!");
      setIsEditing(false);
    } catch {
      toast.error("Failed to save professional details");
    } finally {
      setSaving(false);
    }
  };

	if (routeLoading || loading) {
	  return null; 
	}


  return (
    <main className="dashboard-screen">
      <div className="container-fluid">
        <div className="row gx-5">
          <div className="col-lg-3 left-sidebar-main">
            <LeftSidebar />
          </div>
          <div className="col-lg-9">
            <Header />
            <div className="row contact-section">
              <div className="col-lg-4 profile-sidebar-menu">
                <div className="sec1">
                  <ProfileMenu />
                </div>
              </div>
              <div className="col-lg-8">
                <div className="sec2">
                  <div className="form-section">
                    <div className="row mb-3">
                      <div className="col-sm-8">
                        <h3>Professional Settings</h3>
                        <p>Review your professional details</p>
                      </div>
                      <div className="col-sm-4"></div>
                    </div>

                    {error && (
                      <div className="alert alert-danger alert-dismissible fade show">
                        {error}
                        <button type="button" className="btn-close" onClick={() => setError("")}></button>
                      </div>
                    )}

                    <form>
                      {!isEditing ? (
                        <div className="row g-4">
                          <div className="col-md-6">
                            <label className="form-label">Airline</label>                              
                            <div className="viewField">{airlineLabel}</div>                            
                          </div>

                          <div className="col-md-6">
                            <label className="form-label">Base</label>
                            <div className="viewField">{baseLabel}</div>  
                          </div>

                          <div className="col-md-6">
                            <label className="form-label">Aircraft</label>
                            <div className="viewField">{aircraftLabel}</div> 
                          </div>

                          <div className="col-md-6">
                            <label className="form-label">Role</label>
                            <div className="viewField">{roleLabel}</div>
                          </div>

                          <div className="col-md-6">
                            <label className="form-label">Seat</label>
                            <div className="viewField">{seatLabel}</div>
                          </div>
                        </div>
                      ) : (
                        <div className="row gy-3">
                          <div className="col-md-6">
                            <label className="form-label">Airline</label>
                            <div className="select-wrapper">
                              <select
                                className="form-select custom-select"
                                value={selected.airline_uid || ""}
                                onChange={handleChange("airline_uid")}
                              >
                                <option value="">Airline</option>
                                {options.airlines.map((a) => (
                                  <option key={a.uid} value={a.uid}>
                                    {a.name} {a.iata_code && `(${a.iata_code})`}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <label className="form-label">Base</label>
                            <div className="select-wrapper">
                              <select
                                className="form-select custom-select"
                                value={selected.base_uid || ""}
                                onChange={handleChange("base_uid")}
                              >
                                <option value="">Base</option>
                                {options.bases.map((b) => (
                                  <option key={b.uid} value={b.uid}>
                                    {b.airport_code} {b.city && `- ${b.city}`}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="col-md-6">
                             <label className="form-label">Aircraft</label>
                            <div className="select-wrapper">
                              <select
                                className="form-select custom-select"
                                value={selected.aircraft_uid || ""}
                                onChange={handleChange("aircraft_uid")}
                              >
                                <option value="">Aircraft</option>
                                {options.aircrafts.map((ac) => (
                                  <option key={ac.uid} value={ac.uid}>
                                    {ac.model} {ac.manufacturer && `- ${ac.manufacturer}`}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="col-md-6">
                             <label className="form-label">Role</label>
                            <div className="select-wrapper">
                              <select
                                className="form-select custom-select"
                                value={selected.role_uid || ""}
                                onChange={handleChange("role_uid")}
                              >
                                <option value="">Role</option>
                                {options.roles.map((r) => (
                                  <option key={r.uid} value={r.uid}>
                                    {r.role_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="col-md-12">
                             <label className="form-label">Seat</label>
                            <div className="select-wrapper">
                              <select
                                className="form-select custom-select"
                                value={selected.seat_uid || ""}
                                onChange={handleChange("seat_uid")}
                              >
                                <option value="">Seat</option>
                                {options.seats.map((s) => (
                                  <option key={s.uid} value={s.uid}>
                                    {s.seat_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-5">
                        {isEditing ? (
                          <>
                          <div className="cta-section align-items-center ">
                            <button
                              type="button"
                              className="btn4"
                              onClick={() => setIsEditing(false)}
                              disabled={saving}
                            >
                              Back
                            </button>
                            <div>
                              <button
                                type="button"
                                className="btn5"
                                onClick={handleSave}
                                disabled={saving}
                              >
                                {saving ? "Saving..." : "Save Changes"}
                              </button>
                            </div>
                            </div>
                          </>
                        ) : (
                          <div />
                        )}
                        {!isEditing && (
                          <button
                            type="button"
                            className="btn5"
                            onClick={() => setIsEditing(true)}
                          >
                            Edit Info
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
                <div />
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
